import { Box, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { EN_DASH, signed } from '../growth';
import { GROWTH } from '../tokens';
import { SKILL_LABELS, SKILLS, type Cell, type GrowthClass, type GrowthPage, type GrowthStudentRow, type SchemeLevel, type Skill, type SkillRow } from '../types';
import { CardHead, Chip, GROWTH_CARD_SX, HEADER_CELL_SX, HEADER_ROW_SX, SMALL_SX, STRONG_SX, TEXT_SX, colorOf, labelOf, scoreText } from './cardParts';
import { ClassChip, SearchBox, SwitchButton } from './Controls';

const CELL_WIDTH = 48;
const CHANGE_WIDTH = 56;
const STATUS_WIDTH = 152;
const CHEVRON_WIDTH = 16;

export const ALL_CLASSES = 'all';

export function classLabel(c: GrowthClass): string {
  const period = c.period === null ? '' : ` · Period ${c.period}`;
  return `${c.name}${period} · ${c.students}`;
}

export function visibleStudents(rows: GrowthStudentRow[], classId: string, query: string, hideNoLater: boolean): { shown: GrowthStudentRow[]; hidden: number } {
  const needle = query.trim().toLowerCase();
  let list = rows.filter((row) => classId === ALL_CLASSES || row.classes.includes(classId));
  if (needle) list = list.filter((row) => row.name.toLowerCase().includes(needle));
  const hidden = hideNoLater ? list.filter((row) => !row.hasLater).length : 0;
  if (hideNoLater) list = list.filter((row) => row.hasLater);
  return { shown: list, hidden };
}

function BandCell({ cell, scheme }: { cell: Cell; scheme: SchemeLevel[] }) {
  const base = { width: CELL_WIDTH, height: 24, flexShrink: 0, borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: GROWTH.font, fontSize: '12px', lineHeight: '16px', fontWeight: 700, boxSizing: 'border-box' } as const;
  if (cell.state === 'scored' && cell.band) {
    const label = cell.label || labelOf(scheme, cell.band);
    const score = scoreText(cell);
    return (
      <Box component="span" data-testid="band-cell" data-state="scored" title={label} aria-label={`${score} ${label}`.trim()} data-color={cell.color ?? colorOf(scheme, cell.band)} sx={{ ...base, bgcolor: cell.color ?? colorOf(scheme, cell.band), color: GROWTH.ink, fontVariantNumeric: 'tabular-nums' }}>
        {score}
      </Box>
    );
  }
  if (cell.state === 'unscorable') {
    return (
      <Box component="span" data-testid="band-cell" data-state="unscorable" aria-label="Unscorable" sx={{ ...base, color: GROWTH.disabled }}>
        {EN_DASH}
      </Box>
    );
  }
  if (cell.state === 'submitted') {
    return (
      <Box component="span" data-testid="band-cell" data-state="submitted" aria-label="Submitted, not scored" sx={{ ...base, bgcolor: GROWTH.pending, border: `1px solid ${GROWTH.divider}`, color: GROWTH.muted }}>
        …
      </Box>
    );
  }
  return <Box component="span" data-testid="band-cell" data-state="none" aria-label="Not taken" sx={{ ...base, border: `1px dashed ${GROWTH.moveBorder.same}` }} />;
}

function ChangeCell({ row, skill }: { row: SkillRow; skill: Skill }) {
  const color = row.change === null ? GROWTH.disabled : row.onTrack ? GROWTH.change.up : row.change < 0 ? GROWTH.change.down : GROWTH.muted;
  const weight = row.change === null || (!row.onTrack && row.change >= 0) ? 500 : 700;
  return (
    <Typography component="span" data-testid={`change-${skill}`} data-on-track={row.onTrack ? 'true' : 'false'} sx={{ ...TEXT_SX, width: CHANGE_WIDTH, flexShrink: 0, textAlign: 'right', fontWeight: weight, color, fontVariantNumeric: 'tabular-nums' }}>
      {signed(row.change)}
    </Typography>
  );
}

function Separator() {
  return <Box aria-hidden="true" sx={{ width: '1px', alignSelf: 'stretch', bgcolor: GROWTH.divider, flexShrink: 0 }} />;
}

function SkillHeader({ skill }: { skill: Skill }) {
  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
        <Typography component="b" sx={{ ...HEADER_CELL_SX, textTransform: 'none', letterSpacing: 0, color: GROWTH.ink }}>
          {SKILL_LABELS[skill]}
        </Typography>
        <Box sx={{ display: 'flex', gap: '4px' }}>
          {['BOY', 'MOY', 'EOY'].map((window) => (
            <Box key={window} component="span" sx={{ ...HEADER_CELL_SX, width: CELL_WIDTH, textAlign: 'center' }}>
              {window}
            </Box>
          ))}
        </Box>
      </Box>
      <Box component="span" sx={{ ...HEADER_CELL_SX, width: CHANGE_WIDTH, textAlign: 'right', flexShrink: 0 }}>
        Change
      </Box>
    </>
  );
}

function StudentRow({ student, scheme, onSelect }: { student: GrowthStudentRow; scheme: SchemeLevel[]; onSelect: (id: string) => void }) {
  const status = GROWTH.status[student.status] ?? GROWTH.status['Not started']!;
  return (
    <Box
      component="button"
      type="button"
      data-testid="student-row"
      onClick={() => onSelect(student.id)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        py: '8px',
        width: '100%',
        textAlign: 'left',
        background: 'none',
        border: 0,
        borderBottom: `1px solid ${GROWTH.divider}`,
        px: 0,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'rgba(8, 8, 27, 0.04)' },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <Typography component="b" sx={STRONG_SX}>
          {student.name}
        </Typography>
        {student.tags.map((tag) => {
          const style = GROWTH.tag[tag] ?? GROWTH.status['Not started']!;
          return (
            <Chip key={tag} bg={style.bg} fg={style.fg} testId="student-tag">
              {tag}
            </Chip>
          );
        })}
        {student.also.length > 0 && (
          <Typography component="span" sx={{ ...SMALL_SX, fontWeight: 400 }}>
            {`Also ${student.also.join(', ')}`}
          </Typography>
        )}
      </Box>
      {SKILLS.map((skill) => {
        const row = student[skill];
        return (
          <Box key={skill} sx={{ display: 'contents' }}>
            <Box sx={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
              <BandCell cell={row.boy} scheme={scheme} />
              <BandCell cell={row.moy} scheme={scheme} />
              <BandCell cell={row.eoy} scheme={scheme} />
            </Box>
            <ChangeCell row={row} skill={skill} />
            <Separator />
          </Box>
        );
      })}
      <Box sx={{ width: STATUS_WIDTH, flexShrink: 0 }}>
        <Chip bg={status.bg} fg={status.fg} testId="status-chip">
          {student.status}
        </Chip>
      </Box>
      <ChevronRightIcon aria-hidden="true" sx={{ width: CHEVRON_WIDTH, flexShrink: 0, color: GROWTH.muted, fontSize: 16 }} />
    </Box>
  );
}

export interface StudentFilters {
  classId: string;
  query: string;
  hideNoLater: boolean;
}

export const DEFAULT_FILTERS: StudentFilters = { classId: ALL_CLASSES, query: '', hideNoLater: true };

export function StudentsTable({ page, later, filters, onFilters, onSelect }: { page: GrowthPage; later: string; filters: StudentFilters; onFilters: (filters: StudentFilters) => void; onSelect: (id: string) => void }) {
  const { classId, query, hideNoLater } = filters;
  const setClassId = (value: string) => onFilters({ ...filters, classId: value });
  const setQuery = (value: string) => onFilters({ ...filters, query: value });
  const setHideNoLater = (value: boolean) => onFilters({ ...filters, hideNoLater: value });
  const noLater = page.studentRows.filter((row) => !row.hasLater).length;
  const { shown, hidden } = visibleStudents(page.studentRows, classId, query, hideNoLater);
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <Box data-testid="class-chips" sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <ClassChip label={`All classes · ${page.studentRows.length}`} pressed={classId === ALL_CLASSES} onClick={() => setClassId(ALL_CLASSES)} />
          {page.classes.map((c) => (
            <ClassChip key={c.id} label={classLabel(c)} pressed={classId === c.id} onClick={() => setClassId(c.id)} />
          ))}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <SearchBox value={query} onChange={setQuery} label="Find a student" />
          <SwitchButton label={`Hide no ${later} data · ${noLater}`} pressed={hideNoLater} onChange={setHideNoLater} />
        </Box>
      </Box>
      <Box component="section" data-testid="students-table" sx={{ ...GROWTH_CARD_SX, pb: '16px' }}>
        <CardHead title="Students" sub={page.summary} legend={page.scheme} />
        <Box data-testid="students-header" sx={HEADER_ROW_SX}>
          <Box sx={{ ...HEADER_CELL_SX, flex: 1, minWidth: 0 }}>Student</Box>
          {SKILLS.map((skill) => (
            <Box key={skill} sx={{ display: 'contents' }}>
              <SkillHeader skill={skill} />
              <Separator />
            </Box>
          ))}
          <Box sx={{ ...HEADER_CELL_SX, width: STATUS_WIDTH, flexShrink: 0 }}>{`${later} status`}</Box>
          <Box sx={{ width: CHEVRON_WIDTH, flexShrink: 0 }} />
        </Box>
        {shown.map((student) => (
          <StudentRow key={student.id} student={student} scheme={page.scheme} onSelect={onSelect} />
        ))}
        {shown.length === 0 && (
          <Typography component="p" sx={{ ...TEXT_SX, color: GROWTH.muted, py: '16px', m: 0 }}>
            No students match.
          </Typography>
        )}
        {hidden > 0 && (
          <Box data-testid="hidden-note" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: '16px', borderTop: `1px solid ${GROWTH.divider}` }}>
            <Typography component="span" sx={{ ...TEXT_SX, color: GROWTH.muted }}>
              {`${hidden} ${hidden === 1 ? 'student' : 'students'} with no ${later} data hidden`}
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={() => setHideNoLater(false)}
              sx={{ ...STRONG_SX, color: GROWTH.action, background: 'none', border: 0, p: 0, cursor: 'pointer' }}
            >
              Show
            </Box>
          </Box>
        )}
        <Typography component="span" sx={{ ...SMALL_SX, display: 'inline-flex', alignItems: 'center', gap: '8px', mt: '16px' }}>
          <Box component="i" aria-hidden="true" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '9999px', bgcolor: GROWTH.change.up }} />
          On track for expected growth
        </Typography>
      </Box>
    </>
  );
}
